// Data pro rychlý náhled produktu z upsellu „Hodí se k tomu:" na detailu
// (upsellQuickView.js). Zdrojem je HTML DETAILU toho produktu ze stejné domény —
// ne vyhledávací index: index nenese popis ani galerii a na ostré je celá naše
// vyhledávací vrstva vypnutá stand-down guardem, dokud běží Luigi's Box.
//
// Fetch jede `credentials: 'same-origin'` (na rozdíl od modalu dopravy, který
// chce cookies naopak NEposílat): ceny musí sedět s cenou v řádku upsellu,
// kterou Shoptet vyrenderoval pro přihlášeného zákazníka (velkoobchodní ceník).
// GET detailu nemá vedlejší efekt na košík a blok „naposledy prohlížené" naše
// šablona nikde nekreslí. Cache slibu per href žije po celý pageview.
//
// ⛔ Z fetchnutého dokumentu se NIKDY neadoptují uzly ani nečte `innerHTML` —
// ven jdou jen řetězce (`textContent`, `src`, `href`). Cizí HTML by jinak
// propašovalo `onerror` na obrázku.
//
// ⚠️ Parser se scopuje na `.p-detail-inner` (resp. `.p-data-wrapper`,
// `.p-image-wrapper`, `#description`): fetchnutá stránka nese i vlastní
// související produkty, takže `.price-final`, `.availability` nebo `.p-code`
// na úrovni dokumentu vracejí i cizí karty. Obaly `.tab-pane` (desktop) vs.
// `.shp-accordion-content` (mobilní render) se neadresují vůbec — jen obsahové
// třídy, aby parser sedl na oba rendery (Shoptet servíruje jiné HTML dle UA).

import { parseNumber, discountPercent } from './productPrice.js';
import { nativeDeliveryText } from './deliveryTimeline.js';
import L from '../locale/cs/productPreviewModel.js';

// Výpadek sítě nesmí držet modal v „Načítáme…" donekonečna
const FETCH_TIMEOUT_MS = 8000;
const MAX_PARAS = 4;
const MAX_IMAGES = 6;
const MAX_PARAMS = 30;
// Řádek nativní tabulky, který v náhledu nic neřeší — je to navigace, ne parametr
const SKIP_PARAM_RE = L.skipParamRe;
// Variantní produkt vrací v každém poli zřetězené hodnoty VŠECH variant
// (kód „P500-21/L P500-20/M …", EAN „Zvolte variantu") — taková hodnota lže
const VARIANT_PLACEHOLDER_RE = L.variantPlaceholderRe;

const cache = Object.create(null);

function txt(el) {
  return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

function attr(el, name) {
  const v = el ? el.getAttribute(name) : null;
  return v ? v.trim() : '';
}

/** Lazy obrázky nesou placeholder `data:` v `src` a pravdu v `data-src`. */
function imgSrc(img) {
  const lazy = attr(img, 'data-src');
  if (lazy) return lazy;
  const src = attr(img, 'src');
  return /^data:/.test(src) ? '' : src;
}

// --- dílčí parsery ----------------------------------------------------------

/** Galerie: miniatury nesou velkou fotku v `href`, náhled v `data-src`. */
function parseImages(imgw) {
  const out = [];
  const thumbs = [...imgw.querySelectorAll('.p-thumbnails-inner a.p-thumbnail')];
  thumbs.forEach(function (a) {
    if (out.length >= MAX_IMAGES) return;
    const img = a.querySelector('img');
    const full = attr(a, 'href');
    const thumb = imgSrc(img);
    if (!full && !thumb) return;
    out.push({ full: full || thumb, thumb: thumb || full, alt: attr(img, 'alt') || attr(a, 'data-alt') });
  });
  if (out.length) return out;
  // produkt s jedinou fotkou miniatury nemá
  const main = imgw.querySelector('.p-image a.p-main-image');
  const mainImg = main ? main.querySelector('img') : null;
  const full = attr(main, 'href') || imgSrc(mainImg);
  if (!full) return [];
  return [{ full: full, thumb: imgSrc(mainImg) || full, alt: attr(mainImg, 'alt') }];
}

/** Krátký popis jako pole řetězců — odstavce a odrážky, žádné uzly. */
function parseShortDesc(data) {
  const box = data.querySelector('.p-short-description');
  if (!box) return [];
  const out = [];
  [...box.querySelectorAll('p, li')].forEach(function (el) {
    if (out.length >= MAX_PARAS) return;
    const t = txt(el);
    if (t) out.push({ tag: el.tagName === 'LI' ? 'li' : 'p', text: t });
  });
  if (out.length) return out;
  const plain = txt(box);
  return plain ? [{ tag: 'p', text: plain }] : [];
}

/** Parametry: tabulka popisu v2 má přednost, jinak nativní „Doplňkové parametry". */
function parseParams(detail, desc) {
  const rows = [];
  const seen = Object.create(null);
  const push = function (label, value) {
    const l = label.replace(/:$/, '').trim();
    if (!l || !value || rows.length >= MAX_PARAMS) return;
    if (SKIP_PARAM_RE.test(l) || VARIANT_PLACEHOLDER_RE.test(value)) return;
    const key = l.toLowerCase();
    if (seen[key]) return; // nativní tabulka umí týž parametr dvakrát
    seen[key] = true;
    rows.push({ label: l, value: value });
  };

  const v2 = desc ? [...desc.querySelectorAll('.bs-pc-specs table tr')] : [];
  v2.forEach(function (tr) {
    push(txt(tr.querySelector('th')), txt(tr.querySelector('td')));
  });
  if (rows.length) return { rows: rows, source: 'v2' };

  const native = [...detail.querySelectorAll('.extended-description table.detail-parameters tr')];
  native.forEach(function (tr) {
    const th = tr.querySelector('th');
    push(txt(th && th.querySelector('.row-header-label')) || txt(th), txt(tr.querySelector('td')));
  });
  return { rows: rows, source: rows.length ? 'native' : null };
}

/** Klíčové parametry z popisu v2 — `<strong>` hodnota + `<span>` popisek. */
function parseKeyspecs(desc) {
  if (!desc) return [];
  return [...desc.querySelectorAll('.bs-pc-keyspecs .bs-pc-keyspec')]
    .map(function (k) {
      return { value: txt(k.querySelector('strong')), label: txt(k.querySelector('span')) };
    })
    .filter(function (k) {
      return k.value && k.label;
    })
    .slice(0, 3);
}

/** Hodnocení: průměr z `.sr-only` věty, počet ze `.stars-label` (jako detail). */
function parseRating(header) {
  if (!header) return null;
  const m = txt(header.querySelector('.stars .sr-only')).match(/(\d+(?:[.,]\d+)?)\s*z\s*5/);
  const value = m ? parseFloat(m[1].replace(',', '.')) : 0;
  const c = txt(header.querySelector('.stars-label')).match(/(\d+)/);
  const count = c ? parseInt(c[1], 10) : 0;
  if (!count || !isFinite(value) || value <= 0) return null;
  return { value: value, count: count };
}

/** Sleva: nativní „–29 %", jinak dopočet ze škrtnuté ceny (floor jako Shoptet). */
function parseSavePct(data, imgw, standard, current) {
  const sources = [data.querySelector('.price-save'), imgw && imgw.querySelector('.flags .flag-discount')];
  for (let i = 0; i < sources.length; i += 1) {
    const m = txt(sources[i]).match(/(\d+)\s*%/);
    if (m) return parseInt(m[1], 10);
  }
  return discountPercent(parseNumber(standard), parseNumber(current));
}

// --- parser -----------------------------------------------------------------

/**
 * HTML detailu produktu → data náhledu. `null`, když stránka detail nenese
 * (404, přesměrování na výpis, výpadek).
 */
export function parseProductPreview(html) {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const detail = doc.querySelector('.p-detail');
  if (!detail) return null;
  const inner = detail.querySelector('.p-detail-inner') || detail;
  const data = inner.querySelector('.p-data-wrapper') || inner;
  const imgw = inner.querySelector('.p-image-wrapper') || inner;
  const header = data.querySelector('.p-detail-inner-header') || data;
  const desc = detail.querySelector('#description');

  // Variantní produkt: kód, dostupnost i termín jsou zřetězené přes všechny
  // varianty, takže by v náhledu lhaly — nekreslí se a CTA nabídne detail
  // (variantu kódem do košíku přidat nejde).
  const hasVariants = !!(
    data.querySelector('.p-variants-block .variant-list') ||
    data.querySelector('select[name="priceId"]') ||
    data.querySelector('[data-testid="selectVariant"]')
  );

  const name = txt(header.querySelector('h1')) || attr(detail.querySelector('meta[itemprop="name"]'), 'content');
  if (!name) return null;

  const standard = txt(data.querySelector('.price-standard'));
  const current = txt(data.querySelector('.price-final-holder')) || txt(data.querySelector('.price-final'));
  const availLabel = txt(data.querySelector('.availability-label'));

  return {
    name: name,
    brand: attr(data.querySelector('span[itemprop="brand"] meta[itemprop="name"]'), 'content'),
    code: hasVariants ? '' : txt(data.querySelector('tr.p-code td')),
    priceBefore: standard,
    savePct: parseSavePct(data, imgw, standard, current),
    priceNoVat: txt(data.querySelector('.price-additional')),
    availability: hasVariants
      ? null
      : {
          label: availLabel,
          amount: txt(data.querySelector('.availability-amount')),
          color: attr(data.querySelector('.availability-label'), 'style').replace(/^.*color:\s*/i, '').replace(/;.*$/, ''),
        },
    // nativní termín srovnaný s osou detailu (páteční uzávěrka 14:30, Shoptet zná jen 15:00)
    delivery: hasVariants ? '' : nativeDeliveryText(txt(data.querySelector('.delivery-time'))),
    images: parseImages(imgw),
    shortDesc: parseShortDesc(data),
    intro: desc
      ? {
          claim: txt(desc.querySelector('.bs-pc-intro h2')),
          text: txt(desc.querySelector('.bs-pc-intro p')),
        }
      : null,
    keyspecs: parseKeyspecs(desc),
    params: parseParams(detail, desc),
    rating: parseRating(header),
    hasVariants: hasVariants,
  };
}

// --- fetch ------------------------------------------------------------------

/**
 * Stáhne a rozparsuje detail produktu. Nikdy neodmítá — `null` znamená
 * „data nejsou", volající si drží seed z řádku upsellu a nabídne odkaz.
 */
export function loadProductPreview(href) {
  if (!href || !window.fetch || location.protocol === 'file:') return Promise.resolve(null);
  if (cache[href]) return cache[href];
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  let timer = null;
  const done = function () {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  if (ctrl) {
    timer = setTimeout(function () {
      ctrl.abort();
    }, FETCH_TIMEOUT_MS);
  }
  cache[href] = fetch(href, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
    .then(function (r) {
      return r.ok ? r.text() : '';
    })
    .then(function (html) {
      done();
      return parseProductPreview(html);
    })
    .catch(function () {
      done();
      return null;
    });
  return cache[href];
}
